/** The pocket's bundle list (spec §7.2): the things of one kind behind a bundle row ("38 more topics ›"), grouped by relation,
 * searchable, loaded page by page. Picking one moves the focus there, one step along the trail (mockup pickThing). */
import { useState } from "react";
import { getBundle, type BundleItem, type ThingKind } from "@/api/memory-focus";
import {
  PocketRow,
  PocketSection,
  PocketStatement,
  Spinner,
} from "@/components/ui";
import { usePagedQuery } from "@/hooks/use-paged-query";
import { useT } from "@/i18n";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import {
  capitalize,
  kindWord,
  relationGroupLabel,
  thingDisplayName,
} from "@/lib/memory-thing-kinds";
import { useMemoryStore } from "@/stores/memory-store";
import { useProjectStore } from "@/stores/project-store";
import { bundleTitle } from "./memory-pocket-helpers";
import { MemorySearchField } from "./memory-search-field";
import { ThingAvatar } from "./thing-avatar";
export function PocketBundleLevel({ kind }: { kind: ThingKind }) {
  const t = useT(framesMemoryMessages);
  const view = useMemoryStore((s) =>
    s.focusViewId === s.focusId ? s.focusView : null,
  );
  const projectId = useProjectStore((s) => s.currentProject?.id);
  const [query, setQuery] = useState("");
  const [focusId, q] = [view?.focus?.id ?? null, query.trim()];
  const page = usePagedQuery<BundleItem>(
    focusId ? [focusId, kind, projectId ?? "", q].join("|") : null,
    (cursor) => getBundle(focusId!, kind, projectId, q, cursor),
  );
  if (!view?.focus || !focusId)
    return (
      <div className="flex justify-center py-6">
        <Spinner className="h-4 w-4 text-muted" />
      </div>
    );
  const groups = new Map<string, BundleItem[]>();
  for (const item of page.items) {
    const label = item.facts[0]
      ? relationGroupLabel(item.facts[0], focusId)
      : capitalize(kindWord(kind, 2));
    groups.set(label, [...(groups.get(label) ?? []), item]);
  }
  return (
    <div data-testid="memory-bundle-level">
      <PocketStatement
        size="panel"
        title={bundleTitle(view, kind)}
        line={t("linkedTo", { name: thingDisplayName(view.focus) })}
      />
      <MemorySearchField
        value={query}
        onChange={setQuery}
        placeholder={t("searchKind", { kind: kindWord(kind, 2) })}
        testId="memory-bundle-search"
      />
      {[...groups.entries()]
        .sort((a, b) => b[1].length - a[1].length)
        .map(([label, items]) => (
          <PocketSection key={label} title={label}>
            {items.map(({ thing, facts }) => {
              const id = thing.id;
              return id ? (
                <PocketRow
                  key={id}
                  testId={`memory-bundle-item-${id}`}
                  leading={<ThingAvatar kind={thing.kind} name={thing.name} />}
                  title={thingDisplayName(thing)}
                  secondLine={facts[0]?.claim}
                  onSelect={() =>
                    useMemoryStore
                      .getState()
                      .openThing(id, thingDisplayName(thing), "push")
                  }
                />
              ) : null;
            })}
          </PocketSection>
        ))}
      {page.hasMore ? (
        <PocketRow
          testId="memory-bundle-more"
          title={t("showMore")}
          onSelect={page.loadMore}
        />
      ) : null}
      {page.settled && !page.error && page.items.length === 0 ? (
        <p className="px-2 py-1 text-appkit-caption text-muted">
          {q ? t("bundleNothingCalled", { query: q }) : t("bundleAllOnGlobe")}
        </p>
      ) : null}
      {page.error ? (
        <PocketRow
          testId="memory-bundle-retry"
          title={t("bundleFailed")}
          onSelect={page.retry}
        />
      ) : null}
    </div>
  );
}
